import { Button, ButtonGroup, Modal } from '@ver-uds/react';
import { Alert, Form, Submit, TextInputControl } from '@ver-uds/uswds-react';
import React, { useState } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { Application } from '@vis-auth/vis-user-client';
import { forgotPasswordErrors, ForgotPasswordModalFormValues, userIDLength } from '../../../../constants/Dashboard';
import useLegacyIdpApi from '../../../../services/LegacyIdpApi';
import RequiredLabel from '../../../RequiredLabel';

enum FormStatus {
  INITIAL,
  SUCCESS,
  ERROR,
}

export type DashboardForgotPasswordModalFormProps = {
  formMethods: UseFormReturn<ForgotPasswordModalFormValues>;
  errorMessage: string;
  setErrorMessage: (msg: string) => void;
  onClose: () => void;
};

const DashboardForgotPasswordModalForm = ({
  formMethods,
  errorMessage,
  setErrorMessage,
  onClose,
}: DashboardForgotPasswordModalFormProps): JSX.Element => {
  const [status, setStatus] = useState(FormStatus.INITIAL);

  const legacyIdpApi = useLegacyIdpApi();
  async function sendOTC(userId: string): Promise<void> {
    return legacyIdpApi.sendOTC({
      sendOneTimeCodeRequest: {
        logonId: userId,
        app: Application.EVERIFY,
      },
    });
  }

  // Get form info
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const onSubmit = handleSubmit(async (data: ForgotPasswordModalFormValues): Promise<void> => {
    setErrorMessage('');

    try {
      await sendOTC(data.userId);
      setStatus(FormStatus.SUCCESS);
    } catch (err) {
      setErrorMessage(forgotPasswordErrors.general);
      setStatus(FormStatus.ERROR);
    }
  });

  if (status === FormStatus.SUCCESS) {
    return (
      <>
        <Modal.Content>
          <Alert status="info" slim className="margin-bottom-3">
            <Alert.Text>
              If an account exists for this User ID, instructions to reset your password have been sent to the email
              address associated with the account.
            </Alert.Text>
          </Alert>

          <p>
            Please check your email inboxes as well as spam or junk mail folders for emails sent from
            no-reply@uscis.dhs.gov, as sometimes the emails are mistakenly marked as spam. If you do not receive a
            password reset email, contact{' '}
            <a
              href="mailto:E-Verify@uscis.dhs.gov"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Click here to send an email to E-Verify@uscis.dhs.gov"
            >
              E-Verify@uscis.dhs.gov
            </a>{' '}
            for assistance.
          </p>
        </Modal.Content>
      </>
    );
  }

  return (
    <>
      <Modal.Content>
        <h4>Enter User ID</h4>

        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FormProvider {...formMethods}>
          <Form
            noValidate
            aria-label="Link Existing User Account"
            onSubmit={onSubmit}
            className="link-user-account-form"
          >
            <TextInputControl
              id="userId"
              className="max-width-23"
              label="User ID"
              required
              requiredLabel={<RequiredLabel />}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('userId', {
                required: 'User ID is required.',
                minLength: {
                  value: userIDLength,
                  message: `Must be exactly ${userIDLength} alpha-numeric characters.`,
                },
              })}
              errorMessage={errors?.userId?.message}
              errorId="userId-error"
              maxLength={userIDLength}
              minLength={userIDLength}
            />

            {errorMessage && (
              <Alert status="error" slim className="margin-top-3">
                <Alert.Text>{errorMessage}</Alert.Text>
              </Alert>
            )}

            <ButtonGroup className="margin-0 flex-justify-end">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Submit value="Reset Password" />
            </ButtonGroup>
          </Form>
        </FormProvider>
      </Modal.Content>
    </>
  );
};

export default DashboardForgotPasswordModalForm;
